import type { RegionCode } from "@/api/filter";
import {
  computeConfiguredCardOverride,
  computeConfiguredMachinePriceOverride,
  computeRecommendedConfigurationTotal,
} from "@/utils/compute-configured-investment";
import {
  loadProductSelection,
  type ConfigureProductSelection,
  type ConfigureSelectionItem,
} from "@/utils/configure-storage";

/**
 * POC `machinePriceOverride` for comparison cost preview — reads configured
 * solution total from `configSelectionsByProduct` localStorage (D5).
 */
export function readConfiguredMachinePriceOverride(
  productId: string,
): string | undefined {
  const sel = loadProductSelection(productId);
  if (!sel) return undefined;

  if (sel.optionsTotal != null && sel.optionsTotal > 0) {
    return formatOverrideAmount(sel.optionsTotal);
  }

  const fromItems = sumItemsWithStoredPrices(sel.items);
  if (fromItems) return fromItems;

  return undefined;
}

/**
 * Comparison-card `machinePriceOverride`.
 *
 * CR-006 (supersedes the 2026-06-02 POC-parity decision for configured
 * solutions): once the user explicitly changes or freezes a Configure Solution
 * selection for a product, its comparison card + 6 KPI cards price THAT
 * configuration,
 * and the recommended / "Low invest" ranking re-evaluates off the revised cost.
 * A product the user never configured still prices the fresh AUTO-SELECTED
 * config (POC-exact), so unconfigured cards are unchanged. Only persisted
 * catalog/tooling selections move the card; ad-hoc manual quote lines remain
 * quote-only, and per-fastener tooling stays out of the override (POC parity).
 */
export async function resolveConfiguredMachinePriceOverride(args: {
  productId: string;
  region: RegionCode;
  applicationIds: string[];
  mspAutoFeedEnabled: boolean;
  requiredMachines: number;
  signal?: AbortSignal;
}): Promise<string | undefined> {
  if (!args.applicationIds.length) return undefined;
  const stored = loadProductSelection(args.productId);
  return hasAuthoritativeConfigureSelection(stored)
    ? computeConfiguredCardOverride(args)
    : computeConfiguredMachinePriceOverride(args);
}

/** A hydrated auto draft or manual-lines-only draft has no KPI delta to price. */
export function hasAuthoritativeConfigureSelection(
  stored: ConfigureProductSelection | null,
): boolean {
  return (
    stored?.selectionFrozen === true || (stored?.userOverrides?.length ?? 0) > 0
  );
}

export async function resolveRecommendedConfigurationTotal(args: {
  productId: string;
  region: RegionCode;
  applicationIds: string[];
  mspAutoFeedEnabled: boolean;
  requiredMachines: number;
  signal?: AbortSignal;
}): Promise<string | undefined> {
  if (!args.applicationIds.length) return undefined;
  return computeRecommendedConfigurationTotal(args);
}

/**
 * Cache-buster for `useComparisonCosts`. Optimize Allocation can move an
 * application between products without changing the all-app calculation key,
 * while Configure Solution can change optional rows without changing the
 * assignment. Include assignment, required machines and the persisted draft so
 * the display total cannot reuse a stale popup selection.
 */
export function configuredInvestmentCacheSignature(
  productId: string,
  requiredMachines = 1,
  applicationIds: string[] = [],
): string {
  const assignment = [...applicationIds].sort().join(",");
  const stored = loadProductSelection(productId);
  const draft = stored
    ? JSON.stringify({
        items: [...stored.items].sort((a, b) =>
          a.partNo.localeCompare(b.partNo),
        ),
        profilElementType: stored.profilElementType ?? null,
        mdsFastenerGroup: stored.mdsFastenerGroup ?? null,
        mdsFeederTracks: stored.mdsFeederTracks ?? null,
        mdsForceKn: stored.mdsForceKn ?? null,
      })
    : "none";
  return `selected:rm${Math.max(1, Math.floor(requiredMachines))}:apps:${assignment}:draft:${draft}`;
}

function sumItemsWithStoredPrices(
  items: ReadonlyArray<ConfigureSelectionItem>,
): string | undefined {
  let acc = 0;
  let any = false;
  for (const it of items) {
    const price = it.price;
    if (price == null || !Number.isFinite(price) || price <= 0) continue;
    if (!Number.isFinite(it.qty) || it.qty <= 0) continue;
    acc += price * it.qty;
    any = true;
  }
  if (!any || acc <= 0) return undefined;
  return formatOverrideAmount(acc);
}

function formatOverrideAmount(n: number): string {
  return n.toFixed(4);
}
